/**
 * RenderMethodModal — per-scope render method + settings modal.
 *
 * Opened from the global hamburger menu's "Render Method ▶ Settings…" entry.
 * Exposes two tabs — Viewport (creation workspaces: edit/object/pose/sketch/
 * sculpt/reference) and Render (output workspaces: inking/render).  Each tab
 * edits its scope's `RenderConfig` independently.
 *
 * Controls are filtered to the active method for that scope: a section only
 * renders when the selected view mode actually applies to it.  The
 * `SETTING_COMPATIBILITY` table in `renderConfig.ts` is the single source of
 * truth for which keys go with which method, and this modal drives section
 * visibility from it — no duplicated conditionals.
 *
 * The modal state (open/closed) lives in the Zustand store under
 * `renderMethodModalOpen`, so it can be opened from anywhere (hamburger,
 * keyboard shortcut, etc.) without prop drilling.
 */

import { Monitor, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  RENDER_MODE_LABELS,
  type RenderConfig,
  type RenderScope,
  renderScopeFor,
  settingCompatibleWith,
  VIEW_MODES_BY_SCOPE,
} from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import type { ViewMode } from "../app/storeTypes.ts";
import {
  ENV_MAP_PRESET_KEYS,
  ENV_MAP_PRESETS,
} from "../viewport/envPresets.ts";
import {
  MATCAP_PRESET_KEYS,
  MATCAP_PRESETS,
} from "../viewport/matcapPresets.ts";
import {
  VIEWPORT_BG_PRESET_KEYS,
  VIEWPORT_BG_PRESETS,
  viewportBgCssBackground,
} from "../viewport/viewportBgPresets.ts";
import { IconButton } from "./Button.tsx";
import { ColorPicker } from "./ColorPicker.tsx";
import { ModalHeader, ModalShell } from "./ModalShell.tsx";
import { SwatchGrid } from "./SwatchGrid.tsx";
import {
  Divider,
  LabeledRow,
  SectionLabel,
  SegmentedControl,
  SliderRow,
  TabBar,
  Toggle,
} from "./shared.tsx";
import {
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_CAPTION,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Matcap preview — draws a preset's matcap sphere into a small canvas.
// Extracted from the old HeaderBar render-settings popover so the modal owns
// its own preview rendering without a cross-file dependency.
// ---------------------------------------------------------------------------

function MatcapSwatchContent({
  presetKey,
  size,
}: {
  presetKey: string;
  size: number;
}) {
  const canvasRef = useMemo(
    () => ({ current: null as HTMLCanvasElement | null }),
    [],
  );
  useEffect(() => {
    const preset = MATCAP_PRESETS[presetKey];
    if (!preset || !canvasRef.current) return;
    const tex = preset.create();
    const src = tex.image as HTMLCanvasElement | undefined;
    const ctx = canvasRef.current.getContext("2d");
    if (ctx && src) {
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(src, 0, 0, size, size);
    }
  }, [presetKey, size, canvasRef]);
  return (
    <canvas
      ref={(el) => {
        canvasRef.current = el;
      }}
      width={size}
      height={size}
      style={{ width: size, height: size, display: "block" }}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers bound to a scope
// ---------------------------------------------------------------------------

function useScopedSetter(scope: RenderScope) {
  const setRenderSetting = useStore((s) => s.setRenderSetting);
  return useMemo(
    () =>
      <K extends keyof RenderConfig>(key: K, value: RenderConfig[K]) =>
        setRenderSetting(scope, key, value),
    [setRenderSetting, scope],
  );
}

function useScopedConfig(scope: RenderScope): RenderConfig {
  return useStore((s) => s.renderConfigs[scope]);
}

/**
 * Returns true iff **every** key in `keys` applies to `viewMode`. A section
 * is hidden outright when its primary keys don't apply, avoiding the
 * warning-icon noise of showing inert controls.
 */
function sectionApplies(
  keys: (keyof RenderConfig)[],
  viewMode: ViewMode,
): boolean {
  return keys.every((k) => settingCompatibleWith(k, viewMode));
}

// ---------------------------------------------------------------------------
// Per-tab content
// ---------------------------------------------------------------------------

function RenderScopePanel({ scope }: { scope: RenderScope }) {
  const cfg = useScopedConfig(scope);
  const set = useScopedSetter(scope);
  const modes = VIEW_MODES_BY_SCOPE[scope];
  const viewMode = cfg.viewMode;

  const showMatcap = sectionApplies(["matcapPreset"], viewMode);
  const showWireframeXray = sectionApplies(["wireframeXray"], viewMode);
  const showWireOverlay = sectionApplies(["showWireframe"], viewMode);
  const showAmbient = sectionApplies(
    ["ambientColor", "ambientIntensity"],
    viewMode,
  );
  const showAO = sectionApplies(["aoEnabled"], viewMode);
  const showBloom = sectionApplies(["bloomEnabled"], viewMode);
  const showShadows = sectionApplies(["shadowsEnabled"], viewMode);
  const showEnvMap = sectionApplies(["envMapEnabled"], viewMode);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Render method picker */}
      <SectionLabel>Render Method</SectionLabel>
      <SegmentedControl
        value={viewMode}
        options={modes.map((m) => ({ label: RENDER_MODE_LABELS[m], value: m }))}
        onChange={(v) => set("viewMode", v as ViewMode)}
      />

      {(showWireOverlay || showWireframeXray) && (
        <>
          <Divider />
          <SectionLabel>Overlays</SectionLabel>
          {showWireOverlay && (
            <Toggle
              label="Wireframe Overlay"
              checked={cfg.showWireframe}
              onChange={(v) => set("showWireframe", v)}
            />
          )}
          {showWireframeXray && (
            <Toggle
              label="Wireframe X-Ray (see through)"
              checked={cfg.wireframeXray}
              onChange={(v) => set("wireframeXray", v)}
            />
          )}
        </>
      )}

      {showMatcap && (
        <>
          <Divider />
          <SectionLabel>Matcap</SectionLabel>
          <SwatchGrid
            keys={MATCAP_PRESET_KEYS}
            active={cfg.matcapPreset}
            onSelect={(key) => set("matcapPreset", key)}
            label={(key) => MATCAP_PRESETS[key].label}
            renderSwatch={(key, size) => (
              <MatcapSwatchContent presetKey={key} size={size} />
            )}
          />
        </>
      )}

      {showAmbient && (
        <>
          <Divider />
          <SectionLabel>Ambient Light</SectionLabel>
          <LabeledRow label="Color">
            <ColorPicker
              value={cfg.ambientColor}
              onChange={(hex) => set("ambientColor", hex)}
              size={22}
            />
          </LabeledRow>
          <SliderRow
            label="Intensity"
            value={Math.round(cfg.ambientIntensity * 100)}
            min={0}
            max={200}
            displayValue={cfg.ambientIntensity.toFixed(2)}
            onChange={(v) => set("ambientIntensity", v / 100)}
            dimChromeOnDrag
          />
        </>
      )}

      {showAO && (
        <>
          <Divider />
          <SectionLabel>Ambient Occlusion</SectionLabel>
          <Toggle
            label="Enable"
            checked={cfg.aoEnabled}
            onChange={(v) => set("aoEnabled", v)}
          />
          {cfg.aoEnabled && (
            <SliderRow
              label="Intensity"
              value={Math.round(cfg.aoIntensity * 100)}
              min={10}
              max={300}
              displayValue={cfg.aoIntensity.toFixed(1)}
              onChange={(v) => set("aoIntensity", v / 100)}
              dimChromeOnDrag
            />
          )}
        </>
      )}

      {showBloom && (
        <>
          <Divider />
          <SectionLabel>Bloom</SectionLabel>
          <Toggle
            label="Enable"
            checked={cfg.bloomEnabled}
            onChange={(v) => set("bloomEnabled", v)}
          />
          {cfg.bloomEnabled && (
            <>
              <SliderRow
                label="Intensity"
                value={Math.round(cfg.bloomIntensity * 100)}
                min={0}
                max={300}
                displayValue={cfg.bloomIntensity.toFixed(2)}
                onChange={(v) => set("bloomIntensity", v / 100)}
                dimChromeOnDrag
              />
              <SliderRow
                label="Threshold"
                value={Math.round(cfg.bloomThreshold * 100)}
                min={0}
                max={200}
                displayValue={cfg.bloomThreshold.toFixed(2)}
                onChange={(v) => set("bloomThreshold", v / 100)}
                dimChromeOnDrag
              />
            </>
          )}
        </>
      )}

      {showShadows && (
        <>
          <Divider />
          <SectionLabel>Shadows</SectionLabel>
          <Toggle
            label="Soft Shadows"
            checked={cfg.shadowsEnabled}
            onChange={(v) => set("shadowsEnabled", v)}
          />
          {cfg.shadowsEnabled && (
            <SliderRow
              label="Softness"
              value={cfg.shadowSoftness}
              min={1}
              max={100}
              displayValue={String(cfg.shadowSoftness)}
              onChange={(v) => set("shadowSoftness", v)}
              dimChromeOnDrag
            />
          )}
        </>
      )}

      {showEnvMap && (
        <>
          <Divider />
          <SectionLabel>Environment Map</SectionLabel>
          <Toggle
            label="Enable"
            checked={cfg.envMapEnabled}
            onChange={(v) => set("envMapEnabled", v)}
          />
          {cfg.envMapEnabled && (
            <>
              <LabeledRow label="Preset">
                <select
                  value={cfg.envMapPreset}
                  onChange={(e) => set("envMapPreset", e.target.value)}
                  style={{
                    flex: 1,
                    padding: "3px 6px",
                    borderRadius: RADIUS_SM,
                    border: `0.5px solid ${COLOR_BORDER_STRONG}`,
                    background: COLOR_SURFACE,
                    color: COLOR_TEXT_SECONDARY,
                    fontSize: FONT_SIZE_CAPTION,
                    cursor: "pointer",
                  }}
                >
                  {ENV_MAP_PRESET_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {ENV_MAP_PRESETS[key].label}
                    </option>
                  ))}
                </select>
              </LabeledRow>
              <SliderRow
                label="Intensity"
                value={Math.round(cfg.envMapIntensity * 100)}
                min={0}
                max={300}
                displayValue={cfg.envMapIntensity.toFixed(2)}
                onChange={(v) => set("envMapIntensity", v / 100)}
                dimChromeOnDrag
              />
              <Toggle
                label="Show as Background"
                checked={cfg.envMapBackground}
                onChange={(v) => set("envMapBackground", v)}
              />
            </>
          )}
        </>
      )}

      {/* Viewport background — applies in every method. */}
      <Divider />
      <SectionLabel>Viewport Background</SectionLabel>
      <SwatchGrid
        keys={VIEWPORT_BG_PRESET_KEYS}
        active={cfg.viewportBgPreset}
        onSelect={(key) => set("viewportBgPreset", key)}
        label={(key) => VIEWPORT_BG_PRESETS[key].label}
        renderSwatch={(key, size) => {
          const p = VIEWPORT_BG_PRESETS[key];
          return (
            <div
              style={{
                width: size,
                height: size,
                borderRadius: RADIUS_SM,
                background: viewportBgCssBackground(p),
              }}
            />
          );
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal shell wiring — open/close, default-tab-for-workspace, layout
// ---------------------------------------------------------------------------

export function RenderMethodModal() {
  const open = useStore((s) => s.renderMethodModalOpen);
  const setOpen = useStore((s) => s.setRenderMethodModalOpen);
  const workspaceMode = useStore((s) => s.workspaceMode);

  const defaultTab: RenderScope = renderScopeFor(workspaceMode);
  const [activeTab, setActiveTab] = useState<RenderScope>(defaultTab);

  // Reset to the workspace-appropriate tab each time the modal opens.
  useEffect(() => {
    if (open) setActiveTab(renderScopeFor(workspaceMode));
  }, [open, workspaceMode]);

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={() => setOpen(false)}
      width={640}
      portal
      style={{ maxHeight: "calc(100vh - 80px)" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <ModalHeader icon={Monitor} title="Render Method" />
        <IconButton
          onClick={() => setOpen(false)}
          label="Close"
          icon={<X size={16} />}
          size={28}
          style={{ color: COLOR_TEXT_SECONDARY }}
        />
      </div>

      <TabBar
        tabs={[
          { id: "viewport", label: "Viewport" },
          { id: "render", label: "Render" },
        ]}
        active={activeTab}
        onChange={setActiveTab}
        variant="underline"
      />

      <div
        style={{
          fontSize: FONT_SIZE_TINY,
          color: COLOR_TEXT_SECONDARY,
          paddingBottom: 4,
        }}
      >
        {activeTab === "viewport"
          ? "Applies in Edit, Object, Pose, Sketch, Sculpt, and Reference modes."
          : "Applies in Render and Inking modes."}
      </div>

      <div style={{ overflowY: "auto", maxHeight: "65vh", paddingRight: 8 }}>
        <RenderScopePanel scope={activeTab} />
      </div>

      <div style={{ height: FONT_SIZE_SMALL }} />
    </ModalShell>
  );
}
